"use client";

import React, { useState } from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { usePathname } from "next/navigation";
import { useT } from "@/i18n/useT";
import { useAuth } from "@/lib/useAuth";
import Box from "@mui/material/Box";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"; // Used for Create expandable entry
import HomeIcon from "@mui/icons-material/Home";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CoursesIcon from "@/components/courses/CoursesIcon";
import ModuleIcon from "@/components/modules/ModulesIcon";
import ContentBlockIcon from "@/components/contentBlocks/ContentBlocksIcon";
import StudentsIcon from "@/components/students/studentsIcon";
import ImportIcon from "@/components/import/ImportIcon";
import { useTheme } from "@mui/material/styles";
import { useEffectOnce } from "@/lib/useEffectOnce";
import { useSchool } from "@/lib/useSchool";
import { useNavigation } from "@/lib/navigation";

interface NavLink {
  label: string;
  icon: React.ReactElement;
  href: string;
  requiresTeaching?: boolean;
  requiresAdmin?: boolean;
}

const schoolNavLinks: NavLink[] = [
  {
    label: "courses",
    icon: <CoursesIcon />,
    href: "/courses",
    requiresAdmin: false,
  },
  {
    label: "modules",
    icon: <ModuleIcon />,
    href: "/modules",
    requiresAdmin: true,
  },
  {
    label: "contents",
    icon: <ContentBlockIcon />,
    href: "/contentBlocks",
    requiresAdmin: true,
  },
  {
    label: "import",
    icon: <ImportIcon />,
    href: "/import",
    requiresAdmin: true,
  },
  {
    label: "students",
    icon: <StudentsIcon />,
    href: "/students",
    requiresAdmin: true,
  },
];

// School-specific navigation links
const navLinks: NavLink[] = [{ label: "home", icon: <HomeIcon />, href: "/" }];

const createLinks = [
  { label: "course", href: "/courses/new" },
  { label: "module", href: "/modules/new" },
  { label: "content", href: "/contentBlocks/new" },
];

export default function Sidebar({
  onClose,
  open = true,
  onMouseEnter,
  onMouseLeave,
  headerHeight = 64,
}: {
  onClose?: () => void;
  open?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  headerHeight?: number;
}) {
  const navigation = useNavigation();

  const menuButtonHeight = 48; // Height of the menu button
  const headerWidth = headerHeight;
  const pathname = usePathname();
  const t = useT("ui.nav");
  const theme = useTheme();
  const { user, loading } = useAuth();
  const [themeClass, setThemeClass] = useState(() => {
    if (typeof document !== "undefined") {
      return document.documentElement.className;
    }
    return "";
  });
  const [createOpen, setCreateOpen] = useState(false);
  const { currentUserRole, inSchoolContext } = useSchool();

  // Determine user permissions in school context
  const canTeach = ["owner", "admin", "instructor", "ta"].includes(
    currentUserRole || ""
  );
  const canAdmin = ["owner", "admin"].includes(currentUserRole || "");

  // Choose navigation links based on context
  const activeNavLinks = inSchoolContext ? schoolNavLinks : navLinks;

  useEffectOnce(() => {
    if (typeof window === "undefined") return;
    const html = document.documentElement;
    const observer = new MutationObserver(() => {
      setThemeClass(html.className);
    });
    observer.observe(html, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const drawerWidth = open ? headerWidth * 3.6 : headerWidth;

  // Helper to check if a nav link is selected
  const isSelected = (href: string) => {
    // Exact match or startsWith for subroutes
    return (
      pathname === href ||
      pathname.endsWith(href) ||
      pathname.endsWith(href + "/")
    );
  };

  return (
    <>
      {open && (
        <Box
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            display: { xs: "block", sm: "none" },
            height: "100%",
            width: "100%",
            zIndex: theme.zIndex.drawer - 1,
            background: "rgba(0, 0, 0, 0.5)",
          }}
        />
      )}
      <Drawer
        id="sidebar-drawer-wrapper"
        variant="permanent"
        // set anchor to left if theme is ltr, otherwise right.
        anchor={
          theme.direction === "rtl" ? ("right" as const) : ("left" as const)
        }
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          position: { xs: "absolute", md: "static" },
          width: { xs: open ? drawerWidth : 0, md: drawerWidth },
          transition: "width 0.3s ease-in-out",
          "& .MuiDrawer-paper": {
            width: { xs: open ? drawerWidth : 0, md: drawerWidth },
            transition: "width 0.3s ease-in-out",
            top: headerHeight, // Push drawer down by header height
            height: `calc(100vh - ${headerHeight}px)`, // Reduce height to account for header
            borderInlineEnd: "none",
          },
        }}
      >
        <Box
          id="sidebar-drawer-container"
          sx={{
            width: { xs: open ? drawerWidth : 0, md: drawerWidth },
            display: "flex",
            flexDirection: "column",
            height: "100%",
            transition: "width 0.3s ease-in-out",
          }}
        >
          {/* Navigation Links */}
          <List sx={{ flex: 1 }} disablePadding>
            {/* Render navigation links */}
            {!loading &&
              user &&
              activeNavLinks
                .filter((link) => {
                  // Filter school-specific links based on permissions
                  if (inSchoolContext) {
                    if (link.requiresTeaching && !canTeach) return false;
                    if (link.requiresAdmin && !canAdmin) return false;
                  }
                  return true;
                })
                .map((link, idx) => {
                  const isAfterContents = link.label === "contents";
                  const selected = isSelected(link.href);
                  return (
                    <React.Fragment key={link.label}>
                      <ListItemButton
                        onClick={() => {
                          navigation.navigateTo(link.href);
                        }}
                        selected={selected}
                        sx={{
                          justifyContent: "initial",
                          height: menuButtonHeight,
                          transition: "background 0.2s",
                          mt: idx === 0 ? 0 : undefined,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: headerWidth,
                            justifyContent: "center",
                            fontSize: 24,
                            height: 24,
                            width: 24,
                            display: "flex",
                            alignItems: "center",
                            transition: "all 0.3s ease-in-out",
                            // transform: selected ? "scale(1.2)" : "scale(1)",
                          }}
                        >
                          {link.icon}
                        </ListItemIcon>
                        {open && (
                          <ListItemText
                            primary={t(link.label)}
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              paddingInlineStart: 2,
                            }}
                          />
                        )}
                      </ListItemButton>
                      {isAfterContents && !inSchoolContext && (
                        <>
                          <ListItemButton
                            onClick={() => setCreateOpen((prev) => !prev)}
                            sx={{
                              justifyContent: "initial",
                              height: menuButtonHeight,
                              transition: "background 0.2s",
                              pl: 0,
                              pr: 0,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: headerWidth,
                                justifyContent: "center",
                                fontSize: 24,
                                height: 24,
                                width: 24,
                                display: "flex",
                                alignItems: "center",
                                transition: "all 0.3s ease-in-out",
                              }}
                            >
                              <AddCircleOutlineIcon />
                            </ListItemIcon>
                            {open && (
                              <ListItemText
                                primary={t("create")}
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  paddingInlineStart: 2,
                                  // ...(theme.direction === "rtl"
                                  //   ? { pl: 2 }
                                  //   : { pr: 2 }),
                                }}
                              />
                            )}
                            {open &&
                              (createOpen ? <ExpandLess /> : <ExpandMore />)}
                          </ListItemButton>
                          <Collapse
                            in={createOpen && open}
                            timeout="auto"
                            unmountOnExit
                          >
                            <List component="div" disablePadding>
                              {createLinks.map((sublink) => (
                                <ListItemButton
                                  key={sublink.label}
                                  sx={{
                                    height: headerWidth - 8,
                                    minHeight: headerWidth - 8,
                                    mx: 1,
                                  }}
                                  onClick={() => {
                                    navigation.navigateTo(sublink.href);
                                    if (onClose) onClose();
                                  }}
                                  selected={isSelected(sublink.href)}
                                >
                                  <ListItemText
                                    primary={t(sublink.label)}
                                    sx={{
                                      // ...(theme.direction === "rtl"
                                      //   ? { pr: 6 }
                                      //   : { pl: 6 }),
                                      paddingInlineStart: 7,
                                    }}
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          </Collapse>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
